import React, { useState } from 'react';
import { useWorkflowMetrics } from './hooks/useWorkflowMetrics';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { exportWorkflowMetrics } from '../../../../utils/exportToExcel';
import { IconDownload } from '@tabler/icons-react';

const ROLE_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];
const MODULE_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];

const WorkflowAdoption = () => {
  const [dateRange, setDateRange] = useState('30d');
  const { data, loading, error, refetch } = useWorkflowMetrics({ dateRange, organization: null });

  // Calculate metrics - use totals from API response or sum from frequency_data
  const totalChecks = data.coverageChecksByRole?.total_checks || 0;
  const totalApiCalls = data.apiCallFrequency?.total_api_calls 
    || (data.apiCallFrequency?.frequency_data
      ? data.apiCallFrequency.frequency_data.reduce((sum, item) => sum + item.api_calls, 0)
      : 0);
  
  // Get unique users from role breakdown or aggregate from all roles
  let uniqueUsers = 0;
  if (data.coverageChecksByRole?.role_breakdown?.length > 0) {
    // Sum unique users across all roles (may have overlap, but shows activity level)
    uniqueUsers = data.coverageChecksByRole.role_breakdown.reduce(
      (sum, role) => sum + (role.unique_users || 0), 0
    );
  }

  // Prepare chart data
  const roleChecksPieData = data.coverageChecksByRole?.role_breakdown
    ? data.coverageChecksByRole.role_breakdown.map(item => ({
        name: item.initiated_by_role,  // Short code for pie label
        fullName: item.role_full_name || item.initiated_by_role,  // Full name for tooltip
        value: item.check_count,
        percentage: item.percentage,
        users: item.unique_users
      }))
    : [];

  const moduleAdoptionBarData = data.featureModuleAdoption?.feature_adoption
    ? data.featureModuleAdoption.feature_adoption.map(item => ({
        module: item.feature_module.charAt(0).toUpperCase() + item.feature_module.slice(1),
        views: item.view_count,
        users: item.unique_users,
        percentage: item.percentage
      }))
    : [];

  const apiCallFrequencyData = data.apiCallFrequency?.frequency_data
    ? [...data.apiCallFrequency.frequency_data].reverse().map(item => {
        // Parse date safely - handle both ISO strings and date objects
        const dateStr = item.call_date || item.date;
        let formattedDate = 'Invalid Date';
        try {
          if (dateStr) {
            const parsedDate = new Date(dateStr);
            if (!isNaN(parsedDate.getTime())) {
              formattedDate = parsedDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
            }
          }
        } catch (e) {
          console.error('Error parsing date:', dateStr, e);
        }
        return {
          date: formattedDate,
          'Coverage Checks': item.coverage_checks || 0,
          'MMIT API Calls': item.api_calls,
          'Unique Users': item.unique_users
        };
      })
    : [];

  return (
    <div className="p-6 space-y-6">
      <div className="border-b pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Workflow Adoption</h1>
        <p className="text-gray-600 mt-2">
          Analyze feature usage patterns and role-based workflow engagement
        </p>
      </div>

      {/* Date Range Filter */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => exportWorkflowMetrics(data, dateRange)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-[#2563eb] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <IconDownload size={18} />
          Export to Excel
        </button>
        <select 
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="border border-gray-300 rounded-md px-4 py-2 bg-white text-sm"
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </select>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center justify-between">
          <span>Error loading data: {error.message}</span>
          <button
            onClick={refetch}
            className="ml-4 px-3 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded text-sm font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Metric Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">Completed Coverage Checks</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '...' : totalChecks.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Loading...' : `Successfully completed in ${dateRange}`}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">MMIT API Calls</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '...' : totalApiCalls.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Loading...' : `~${Math.round(totalApiCalls / Math.max(totalChecks, 1))} calls per check`}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">Unique Users</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '...' : uniqueUsers}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Loading...' : 'Active users'}
          </p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Coverage Checks by Role</h3>
          {loading ? (
            <div className="h-64 flex items-center justify-center text-gray-400">
              Loading role breakdown...
            </div>
          ) : roleChecksPieData.length > 0 ? (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={roleChecksPieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {roleChecksPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={ROLE_COLORS[index % ROLE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-3 rounded shadow-lg border border-gray-200">
                            <p className="font-semibold text-gray-800">{data.fullName}</p>
                            <p className="text-sm text-gray-600">Checks: {data.value.toLocaleString()}</p>
                            <p className="text-sm text-gray-600">Percentage: {data.percentage.toFixed(1)}%</p>
                            <p className="text-sm text-gray-600">Unique Users: {data.users}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 text-sm text-gray-600">
                Completed Checks: {totalChecks.toLocaleString()}
              </div>
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
              <p>No role data available</p>
              <p className="text-sm mt-2">Data will appear as coverage checks are performed</p>
            </div>
          )}
        </div>
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Feature Module Adoption</h3>
          {loading ? (
            <div className="h-64 flex items-center justify-center text-gray-400">
              Loading module adoption...
            </div>
          ) : moduleAdoptionBarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={moduleAdoptionBarData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="module" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="views" fill="#3b82f6" name="Total Views" />
                <Bar dataKey="users" fill="#8b5cf6" name="Unique Users" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
              <p>No module data available</p>
              <p className="text-sm mt-2">Data will appear as users interact with features</p>
            </div>
          )}
        </div>
      </div>

      {/* Coverage Check & API Call Frequency Chart */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Daily Coverage Activity & MMIT API Usage</h3>
        {loading ? (
          <div className="h-64 flex items-center justify-center text-gray-400">
            Loading coverage check data...
          </div>
        ) : apiCallFrequencyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={apiCallFrequencyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="Coverage Checks" stroke="#3b82f6" strokeWidth={2} />
              <Line yAxisId="left" type="monotone" dataKey="MMIT API Calls" stroke="#8b5cf6" strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="Unique Users" stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex flex-col items-center justify-center text-gray-400">
            <p>No coverage check data available</p>
            <p className="text-sm mt-2">Data will appear as coverage checks are performed</p>
          </div>
        )}
      </div>

      {/* Role Activity Breakdown Table */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Role Activity Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Check Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unique Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Percentage
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
                    Loading role activity data...
                  </td>
                </tr>
              ) : data.coverageChecksByRole?.role_breakdown?.length > 0 ? (
                data.coverageChecksByRole.role_breakdown.map((role, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {role.role_full_name || role.initiated_by_role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {role.check_count.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {role.unique_users}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-2" style={{ maxWidth: '100px' }}>
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${role.percentage}%` }}
                          ></div>
                        </div>
                        <span className="font-medium">{role.percentage.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
                    No role activity data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WorkflowAdoption;
